/**
 * Belgian energy add-ons for the open project (@elec/calc energy.ts): the capacity tariff
 * (Flanders) before and after its big loads, with load management, and the data for the grid
 * operator (PDF) when PV, a battery, a charger or a heat pump is connected.
 */
import { useMemo, useState } from 'react';
import type { Analysis } from '@elec/analysis';
import {
  capacityEstimate,
  CAPACITY_TARIFFS_2026,
  gridNotice,
  gridOperatorFor,
  peakLoads,
  type PeakLoad,
} from '@elec/calc';
import type { Project } from '@elec/domain';
import type { Locale, Translate } from '@elec/i18n';
import { localeTag } from '@elec/i18n';
import { Dialog } from './ui.tsx';

const OPERATORS = ['Fluvius', 'Sibelga', 'ORES', 'RESA'] as const;

export function EnergyDialog(props: {
  project: Project;
  analysis: Analysis;
  t: Translate;
  locale: Locale;
  savePdf(bytes: Uint8Array, name: string): void;
  onClose(): void;
}) {
  const { t, project, locale } = props;
  const eur = (n: number) =>
    n.toLocaleString(localeTag(locale), { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
  const kw = (n: number) => `${n.toLocaleString(localeTag(locale), { maximumFractionDigits: 1 })} kW`;
  const [loads, setLoads] = useState<PeakLoad[]>(() => peakLoads(project, props.analysis));
  const [base, setBase] = useState(4);
  const [area, setArea] = useState(CAPACITY_TARIFFS_2026[1]!.area);
  const three = /400/.test(project.meta.supplyVoltage);
  const tariff = CAPACITY_TARIFFS_2026.find((x) => x.area === area)!.eurPerKwYear;
  const est = useMemo(
    () => capacityEstimate(loads, { basePeakKw: base, eurPerKwYear: tariff, evMinKw: three ? 4.1 : 1.4 }),
    [loads, base, tariff, three],
  );
  const address = project.meta.installationAddress || project.meta.ownerAddress;
  const guess = gridOperatorFor(address);
  const notice = useMemo(() => gridNotice(project), [project]);
  const [operator, setOperator] = useState<string>(guess?.operator ?? 'Fluvius');
  const [ean, setEan] = useState('');
  const [inverter, setInverter] = useState('');
  // From the diagram (batteries drawn on the PV sheet), editable.
  const [batteryKwh, setBatteryKwh] = useState(() => notice.batteryKwh);
  const [batteryKw, setBatteryKw] = useState(0);
  const [date, setDate] = useState('');
  const flanders = !guess || guess.region === 'Flanders';
  const pdf = async () => {
    const x = await import('@elec/export');
    props.savePdf(
      await x.gridNoticePdf(
        project,
        { ...notice, operator, ean, inverter, batteryKwh, batteryKw, date },
        project.meta.locale,
      ),
      `${project.meta.title || 'Tablo'} - ${operator}.pdf`,
    );
  };
  return (
    <Dialog
      title={t('energy.title')}
      wide
      onClose={props.onClose}
      actions={
        <button className="primary" onClick={props.onClose}>
          {t('dialog.close')}
        </button>
      }
    >
      <section className="energy" data-testid="energy-capacity">
        <h3>{t('energy.capacityTitle')}</h3>
        {!flanders && <p className="muted small">{t('energy.notFlanders')}</p>}
        <div className="instr-columns">
          <label className="small">
            {t('energy.basePeak')}
            <input
              type="number"
              min={0}
              step={0.1}
              value={base}
              onChange={(e) => setBase(Number(e.target.value) || 0)}
              data-testid="energy-base"
            />
          </label>
          <label className="small">
            {t('energy.area')}
            <select value={area} onChange={(e) => setArea(e.target.value)}>
              {CAPACITY_TARIFFS_2026.map((a) => (
                <option key={a.area} value={a.area}>
                  {a.area} — {a.eurPerKwYear.toLocaleString(localeTag(locale))} €/kW
                </option>
              ))}
            </select>
          </label>
        </div>
        {loads.length === 0 ? (
          <p className="muted">{t('energy.noLoads')}</p>
        ) : (
          <ul className="energy-loads">
            {loads.map((l, i) => (
              <li key={i}>
                <label>
                  <input
                    type="checkbox"
                    checked={l.coincident}
                    onChange={(e) =>
                      setLoads((ls) =>
                        ls.map((x, k) => (k === i ? { ...x, coincident: e.target.checked } : x)),
                      )
                    }
                  />{' '}
                  {l.name} — {kw(l.kw)}
                </label>
              </li>
            ))}
          </ul>
        )}
        <table className="energy-table" data-testid="energy-result">
          <tbody>
            <tr>
              <th>{t('energy.before')}</th>
              <td>{kw(est.before.peakKw)}</td>
              <td>
                {eur(est.before.eur)} / {t('energy.year')}
              </td>
            </tr>
            <tr>
              <th>{t('energy.after')}</th>
              <td>{kw(est.after.peakKw)}</td>
              <td>
                {eur(est.after.eur)} / {t('energy.year')}
              </td>
            </tr>
            {est.managed && (
              <tr>
                <th>{t('energy.managed', { kw: kw(est.managed.evLimitKw) })}</th>
                <td>{kw(est.managed.peakKw)}</td>
                <td>
                  {eur(est.managed.eur)} / {t('energy.year')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {est.managed && est.after.eur - est.managed.eur >= 1 && (
          <p data-testid="energy-advice">
            {t('energy.advice', { eur: eur(est.after.eur - est.managed.eur), kw: kw(est.managed.evLimitKw) })}
          </p>
        )}
        <p className="muted small">{t('energy.hint')}</p>
      </section>

      <section className="energy" data-testid="energy-grid">
        <h3>{t('energy.gridTitle')}</h3>
        <p className="small">
          {guess
            ? t(guess.toCheck ? 'energy.operatorCheck' : 'energy.operator', { op: guess.operator })
            : t('energy.noPostcode')}
        </p>
        {!notice.any && <p className="muted small">{t('energy.nothingToTell')}</p>}
        <div className="instr-columns">
          <label className="small">
            {t('energy.operatorLabel')}
            <select
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              data-testid="energy-operator"
            >
              {OPERATORS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </label>
          <label className="small">
            EAN
            <input
              value={ean}
              onChange={(e) => setEan(e.target.value.replace(/\s/g, ''))}
              placeholder="5414…"
            />
          </label>
          <label className="small">
            {t('energy.inverter')}
            <input value={inverter} onChange={(e) => setInverter(e.target.value)} />
          </label>
          <label className="small">
            {t('energy.batteryKwh')}
            <input
              type="number"
              min={0}
              step={0.1}
              value={batteryKwh || ''}
              onChange={(e) => setBatteryKwh(Number(e.target.value) || 0)}
            />
          </label>
          <label className="small">
            {t('energy.batteryKw')}
            <input
              type="number"
              min={0}
              step={0.1}
              value={batteryKw || ''}
              onChange={(e) => setBatteryKw(Number(e.target.value) || 0)}
            />
          </label>
          <label className="small">
            {t('energy.date')}
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>
        <p className="small muted">
          {[
            notice.pvKwp ? `PV ${kw(notice.pvKwp).replace('kW', 'kWc')}` : '',
            notice.inverterKva ? `${t('energy.inverter')} ${notice.inverterKva} kVA` : '',
            notice.evKw ? `${t('energy.ev')} ${kw(notice.evKw)}` : '',
            notice.heatPumpKw ? `${t('energy.heatPump')} ${kw(notice.heatPumpKw)}` : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <button onClick={() => void pdf()} data-testid="energy-pdf">
          {t('energy.pdf', { op: operator })}
        </button>
      </section>
    </Dialog>
  );
}
